import React from 'react';
import {
  BasicContainer,
  BodyTextLarge,
  BodyTextSmall,
  FlexCol,
  FlexDynamic,
  FlexRow,
  H2Text,
  H3Text,
  ToggleCheckbox,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { GetTitleParams } from '../usePaywall';
import { PaywallLocation } from '../enums/PaywallLocation';

export interface SelectPlanHeadProps {
  t: (key: string) => string;
  isAnnual: boolean;
  setIsAnnual: (isAnnual: boolean) => void;
  lessMd?: boolean;
  paywallLocation?: PaywallLocation;
  isModal?: boolean;
  getTitle?: (_: GetTitleParams) => string | React.ReactNode;
  enableAnnualRenewal?: boolean;
  isReactivate?: boolean;
  hideIsAnnualToggle?: boolean;
}

export const BestDeal: React.FC<{ text: string }> = ({ text }) => {
  return (
    <BasicContainer
      className="cn_atom_rounded-lg cn_shadow-md"
      bgColor="paywallBestDealBg"
      borderPlacement="all"
      borderSize={0.1}
      borderColor="paywallTrialBadge"
      borderRadius="md"
    >
      <BodyTextSmall fontWeight="medium" className="cn_atom_p-1">
        {text}
      </BodyTextSmall>
    </BasicContainer>
  );
};

export const SelectPlanHead: React.FC<SelectPlanHeadProps> = ({
  isAnnual,
  t,
  setIsAnnual,
  lessMd,
  getTitle,
  paywallLocation,
  isModal,
  enableAnnualRenewal,
  isReactivate,
  hideIsAnnualToggle = false,
}) => {
  return (
    <FlexCol justifyContent="center" alignItems="center" gap="2" className="cn_w-full">
      <FlexDynamic
        direction="column"
        gap="0"
        justifyContent="space-between"
        alignItems="center"
        mdProps={{ direction: 'row', gap: '2' }}
        className="cn_w-full"
        grow={0}
        shrink={0}
      >
        <FlexDynamic
          direction="row"
          gap="2"
          justifyContent="space-between"
          alignItems="center"
          mdProps={{ direction: 'row', gap: '4', justifyContent: 'flex-end', alignItems: 'center' }}
          className="cn_my-4 cn_w-full cn_sm:w-auto"
        >
          {!(lessMd && isModal) && !enableAnnualRenewal && (
            <FlexRow alignItems="center">
              <H3Text fontWeight="medium" lineHeight="default">
                {getTitle({ paywallLocation, isReactivate })}
              </H3Text>
            </FlexRow>
          )}
          {enableAnnualRenewal && (
            <FlexDynamic
              direction="column"
              gap="4"
              mdProps={{ direction: 'row', justifyContent: 'flex-start' }}
              wrap="nowrap"
              justifyContent="space-between"
              alignItems="center"
              className="cn_w-auto cn_mb-4"
            >
              <Icon name="annual-renewal-undraw" width={10} height={4} />
              <FlexDynamic
                direction="column"
                gap="4"
                mdProps={{ direction: 'column', justifyContent: 'flex-start', alignItems: 'flex-start' }}
                wrap="nowrap"
                justifyContent="space-between"
                alignItems="center"
                className="cn_w-auto"
              >
                <H2Text fontWeight="medium" as="h3" align="center">
                  {t('common:paywall.renewal.title')}
                </H2Text>
                <BodyTextLarge as="p" align="center" color="disabled">
                  {t('common:paywall.renewal.subtitle')}
                </BodyTextLarge>
              </FlexDynamic>
            </FlexDynamic>
          )}
          {!hideIsAnnualToggle && (
            <ToggleCheckbox
              checked={isAnnual}
              onChange={() => setIsAnnual(!isAnnual)}
              toggleOffText={t('common:paywall.payMonthly')}
              toggleOnText={t('common:paywall.payYearly')}
              className="cn_w-my-4"
              ariaLabel={isAnnual ? 'View Monthly Plans' : 'View Annual Plans'}
            >
              <BestDeal text={t('common:paywall.bestDeal')} />
            </ToggleCheckbox>
          )}
        </FlexDynamic>
      </FlexDynamic>
    </FlexCol>
  );
};
